import { useCallback, useMemo } from 'react';
import { message } from 'antd';
import { atom, useAtom, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useQueries } from 'react-query';
import { cloneDeep, get, assignWith } from 'lodash';
import update from 'immutability-helper';
import {
  allFiltersSelector,
  selectedMetricsSelector,
  selectedDimensionSelector,
  appliedSortSelector,
} from '@store/metricLens/base/selectors';
import { dimensionsListSelector } from '@store/common/dimensionList/selectors';
import { BASE_METRICS, INVERTED_METRIC_SWITCH_MAP } from '@utils/metrics';
import { combineWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import { createFilterRule } from '@utils/filter';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import getMetricsData from '@src/api/metricContainer';
import { formatTimeseriesData } from '@components/metrics-panel-hz/utils/format';

export const MAX_ACTIVE_TS_ROWS = 10;

// timeseries mode atoms
export const activeTsRowsAtom = atom(0);
export const tsAtomMap = new Map();

// hook to reset state
export const useClearTsRows = () => {
  const setCount = useSetAtom(activeTsRowsAtom);
  const clearFn = useCallback(() => {
    setCount(0);
    tsAtomMap.clear();
  }, [setCount]);
  return clearFn;
};

// reset on filter and dimension change
export const useManageTs = () => {
  const dimension = useSelector(selectedDimensionSelector);
  const filters = useSelector(allFiltersSelector);
  const sortBy = useSelector(appliedSortSelector);

  const clearTsRow = useClearTsRows();
  // clear timseries selections
  useMemo(() => {
    clearTsRow();
  }, [dimension, filters, sortBy]);
};

// manage row level timeseries state
export const useTsRow = (id) => {
  if (!tsAtomMap.has(id)) {
    tsAtomMap.set(id, atom(false));
  }
  const tsRowAtom = tsAtomMap.get(id);
  const [tsMode, setTsMode] = useAtom(tsRowAtom);
  const [activeCount, setActiveCount] = useAtom(activeTsRowsAtom);

  const setHoc = useCallback(
    (value) => {
      // set to timeseries
      if (value && !tsMode) {
        if (activeCount >= MAX_ACTIVE_TS_ROWS) {
          message.warning(`Reached max limit of ${MAX_ACTIVE_TS_ROWS} active timeseries rows.`);
          return;
        }
        setTsMode(value);
        setActiveCount((c) => c + 1);
      }
      // unset from timeseries
      else if (!value && tsMode) {
        setTsMode(value);
        setActiveCount((c) => c - 1);
      }
    },
    [activeCount, setActiveCount, tsMode, setTsMode],
  );

  return [tsMode, setHoc];
};

const metricHistoricalTimeseriesRequestBody = {
  queries: [
    {
      type: 'timeseries',
      dataset: 'ExperienceInsights',
      metrics: ['VideoStartFailuresPercentage'],
      interval: '2021-05-17T10:04:00+08:00/2021-05-17T16:04:13+08:00',
      granularity: 'PT1H',
      filter: [],
      options: { withTotals: true },
    },
  ],
};

// build timeseries query for a row
function useTsQuery({ row }) {
  const metrics = useSelector(selectedMetricsSelector);
  const filters = useSelector(allFiltersSelector);
  const dimensionList = useSelector(dimensionsListSelector);
  const selectedDimension = useSelector(selectedDimensionSelector);

  let queries = [];

  if (dimensionList.length > 0 && 'id' in row) {
    queries = filters.map((eachFilter) => {
      const invertedMetrics: any = [];
      const metricsList = combineWithMetricDeDuplication(metrics, BASE_METRICS);

      const dimensionObj = dimensionList.find((d) => d.groupBy === selectedDimension);
      const drilldownRule = createFilterRule(
        dimensionList,
        dimensionObj.name,
        get(row, 'name[0]', ''),
        get(row, 'value[0]', ''),
      );

      // add drilldown rule to current filter rule
      const combinedRules = update(eachFilter.filter.rule, {
        $push: [[drilldownRule]],
      });

      const newRules = getRulesFromFilter(combinedRules, true);
      const newFilter = getFilterFromRules(newRules, true);

      metricsList.forEach((m) => {
        if (m in INVERTED_METRIC_SWITCH_MAP) {
          invertedMetrics.push(INVERTED_METRIC_SWITCH_MAP[m]);
        }
      });
      const queryMetrics = [...metricsList, ...invertedMetrics];

      const body = cloneDeep(metricHistoricalTimeseriesRequestBody);
      body.queries[0].metrics = queryMetrics;
      body.queries[0].filter = newFilter.rule;
      body.queries[0].interval = `${moment(eachFilter.interval.from).format()}/${moment(eachFilter.interval.to).format()}`;
      body.queries[0].granularity = eachFilter.granularity;
      return { query: body, meta: { ...eachFilter, metrics: queryMetrics } };
    });
    return queries;
  }
  return [];
}

// get timeseries data for a row
function useTimeseries({ row, enabled = false, metric = null }) {
  const queryList = useTsQuery({ row });

  const resList = useQueries(
    queryList.map(({ query, meta }) => {
      return {
        queryKey: ['ml-ts', query],
        queryFn: () => getMetricsData(query),
        select: (response) => {
          const data = get(response, ['data', 0, 'data'], []);
          const { interval, granularity, metrics } = meta;
          const parsedData = formatTimeseriesData({
            metrics,
            interval: { ...interval, granularity },
            data,
          });
          return parsedData;
        },
        enabled: enabled,
        refetchOnWindowFocus: false,
        staleTime: 1 * 60 * 1000, // 1m
      };
    }),
  );

  let isLoading = false;
  const metrics = get(queryList, [0, 'meta', 'metrics'], []);

  const chartData: any = {};
  metrics.forEach((m) => {
    chartData[m] = [];
  });

  const formattedData: any[] = [];
  resList.forEach(({ isFetching, data }) => {
    formattedData.push(data);
    isLoading = isLoading || isFetching;
  });

  const flatten = (x, y) => {
    x.push(y);
    return x;
  };
  const flatData: {} = assignWith(chartData, ...formattedData, flatten);

  return { data: metric ? flatData[metric] : formattedData, isLoading };
}

export default useTimeseries;
